// AutoresDestaque.js — Faixa "Autores Publicados Em Destaque" (home).
// Mostra AUTORES REAIS que publicaram recentemente:
//   1) /ebooks-recentes dá a ordem de recência (ids dos livros mais novos)
//   2) /ebook traz os autores de cada livro (autores: [{id, nome, has_profile}])
//   3) cruzamos os dois → autores reais (com perfil) dos livros mais recentes
// Sem placeholders fake: se não houver dados, a seção não é exibida.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useHomeConfig from "../../hooks/useHomeConfig";
import Reveal from "../ui/Reveal";
import RevealStagger from "../ui/RevealStagger";
import MarcaPadrao from "../ui/MarcaPadrao";
import { TituloDeSecao } from "../ui/TituloDeSecao";

const API_URL = process.env.REACT_APP_API;

// Resolve foto se existir (a maioria dos autores não tem → usamos as iniciais)
const fotoDe = (a) => {
  const f = a.foto || a.imagem || a.avatar || a.profile_image || a.image || null;
  if (!f) return null;
  return /^https?:\/\//i.test(f) ? f : `${API_URL}public/${f}`;
};


const AutoresDestaque = () => {
  const [autores, setAutores] = useState([]);
  const main = process.env.REACT_APP_MAIN;
  const config = useHomeConfig();

  useEffect(() => {
    const carregar = async () => {
      // Aguarda o CMS carregar antes de decidir (senão exibiria o fallback
      // automático por um instante mesmo havendo autores escolhidos).
      if (!config) return;

      const selecionados = Array.isArray(config.autores_destaque) ? config.autores_destaque : [];

      // O admin escolheu autores no painel → eles mandam. Mostra EXATAMENTE
      // esses (na ordem escolhida), sem completar com aleatórios. É isto que
      // diferencia "salvou e apareceu" de "veio autor aleatório": antes, só
      // respeitava a escolha se houvesse 5+ selecionados; menos que isso caía
      // no fallback e misturava autores computados dos livros recentes.
      if (selecionados.length > 0) {
        setAutores(selecionados);
        return;
      }

      // Nenhum autor escolhido no CMS → fallback automático: usa os autores de
      // MAIOR DESTAQUE (dos livros mais recentes) para a seção não ficar vazia.
      let computados = [];
      try {
        const [rRecentes, rEbooks] = await Promise.all([
          fetch(API_URL + "ebooks-recentes"),
          fetch(API_URL + "ebook"),
        ]);
        const recentes = rRecentes.ok ? await rRecentes.json() : [];
        const ebooks = rEbooks.ok ? await rEbooks.json() : [];
        if (Array.isArray(ebooks)) {
          const byId = new Map(ebooks.map((e) => [e.id, e]));
          const ordem = Array.isArray(recentes) && recentes.length
            ? recentes.map((e) => e.id)
            : [...ebooks].sort((a, b) => (b.id || 0) - (a.id || 0)).map((e) => e.id);
          const vistosC = new Set();
          for (const id of ordem) {
            const livro = byId.get(id);
            const autoresLivro = Array.isArray(livro?.autores) ? livro.autores : [];
            for (const a of autoresLivro) {
              const aid = typeof a === "object" ? a?.id : null;
              if (aid == null || vistosC.has(aid)) continue;
              if (a.has_profile === false) continue; // só autores com perfil real
              vistosC.add(aid);
              computados.push(a);
            }
          }
        }
      } catch (e) {
        console.error("Erro ao buscar autores em destaque:", e);
      }

      // Fallback: mostra até 5 autores de maior destaque calculados.
      setAutores(computados.slice(0, 5));
    };
    carregar();
  }, [config]);

  if (autores.length === 0) return null; // sem autores reais → não exibe a seção

  // `pb-20` e não `py-10`: o cartão de papel cresce para FORA da seção quando
  // abre (ver .card-autor-acao no App.css) — 46px de cartão mais 12px de sombra
  // sólida. Com os 40px de antes ele entrava na faixa laranja logo abaixo.
  // O respiro fica reservado sempre, e não só no hover: espaço que aparece e
  // some é o mesmo empurrão de que se estava fugindo. Números >= 6 também
  // escapam da escala de espaçamento que o Bootstrap redefine com !important.
  return (
    <section className="px-4 md:px-8 pt-10 pb-20">
      <div className="max-w-[1280px] mx-auto">
        <Reveal className="mb-[2.25rem]">
          <TituloDeSecao
            chapeu="Quem escreve"
            titulo="Autores publicados em destaque"
            destaque="em destaque"
            subtitulo="Pesquisadores que publicam com a Insigne Acadêmica. Toque no cartão para ver a obra de cada um."
          />
        </Reveal>

        <RevealStagger className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 md:gap-6 orfa-ao-centro">
          {autores.map((autor) => {
            const foto = fotoDe(autor);
            return (
              <RevealStagger.Item key={autor.id} className="h-full">
                <Link
                  to={main + "autores/" + autor.id}
                  onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                  className="card-autor h-full"
                >
                  {/* Rabiscos decorativos */}
                  <svg className="rabisco estrela" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 2L15 9L22 10L17 15L18.5 22L12 18.5L5.5 22L7 15L2 10L9 9L12 2Z" />
                  </svg>
                  <svg className="rabisco faisca" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 0C12 6.6 17.4 12 24 12C17.4 12 12 17.4 12 24C12 17.4 6.6 12 0 12C6.6 12 12 6.6 12 0Z" />
                  </svg>
                  <svg className="rabisco espiral" viewBox="0 0 100 100" aria-hidden="true">
                    <path d="M50 10C27.9 10 10 27.9 10 50C10 72.1 27.9 90 50 90C72.1 90 90 72.1 90 50C90 32.3 75.7 18 58 18C44.3 18 33 29.3 33 43C33 53.5 41.5 62 52 62C59.7 62 66 55.7 66 48" />
                  </svg>

                  {/* Foto real do autor; sem foto, a marca da Insigne */}
                  <div className="card-autor-foto">
                    {foto ? (
                      <img src={foto} alt={autor.nome} loading="lazy" className="w-full h-full object-cover" />
                    ) : (
                      <MarcaPadrao />
                    )}
                  </div>

                  {/* Nome e selo são elementos IRMÃOS, não aninhados: com o
                      selo dentro do <p>, a altura mínima do nome valia para os
                      dois juntos e o selo continuava dançando entre cartões. */}
                  <p className="card-autor-nome" title={autor.nome}><span>{autor.nome}</span></p>
                  <span className="card-autor-cargo">Autor</span>

                  {/* Aparece no hover, no lugar dos botões de rede social do
                      componente original — que não fazem sentido aqui. */}
                  <span className="card-autor-acao">
                    <span className="card-autor-btn">Ver mais</span>
                  </span>
                </Link>
              </RevealStagger.Item>
            );
          })}
        </RevealStagger>
      </div>
    </section>
  );
};

export default AutoresDestaque;
